import { MissionObject } from '../../blocks/mission'
import { getCookieName } from '../../utils/cookie'
import { generateProjectTitle } from '../../utils/projectTitleUtils'
import { addBreadcrumb } from '../../config/sentry'

const getCookie = (name: string) => {
  return (
    document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${name}=`))
      ?.split('=')[1] ?? ''
  )
}

const getToken = () => {
  const cookieName = getCookieName()
  const token = getCookie(cookieName)

  if (!token) {
    throw new Error('Failed to get token')
  }

  return token
}

interface ShortProjectInfo {
  isPublic?: boolean
  missionId: string
  title: string
  userId: string
  isCopy?: boolean
  originalId?: string
}

export interface ProjectInfo extends ShortProjectInfo {
  createdAt: string
  projectId: string
  robloxUserId: string
  updatedAt: string
}

interface PartialProjectInfo extends Partial<ShortProjectInfo> {
  userId: string
}

export const createProject = async (
  info: ShortProjectInfo,
): Promise<string> => {
  if (location.hostname === 'localhost') {
    return `${info.missionId}`
  }

  const endpoint = import.meta.env.VITE_LAMBDA_ENDPOINT
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      mission_id: info.missionId,
      title: info.title,
      original_id: info.originalId,
      is_copy: info.isCopy,
    }),
  })
  if (res.ok) {
    const data = await res.json()
    if (Object.prototype.hasOwnProperty.call(data, 'project_id')) {
      return data.project_id
    }
  } else {
    console.error('Failed to create project:', res)
  }

  throw new Error('Failed to create project')
}

export const deleteProject = async (projectId: string): Promise<boolean> => {
  if (location.hostname === 'localhost') {
    return false
  }

  const endpoint = `${import.meta.env.VITE_LAMBDA_ENDPOINT}/${projectId}`
  const res = await fetch(endpoint, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  })
  return res.ok
}

export const getProjectList = async (
  userId: string,
): Promise<Array<ProjectInfo>> => {
  if (location.hostname === 'localhost') {
    return new Promise((resolve) => {
      setTimeout(async () => {
        const projects = await Promise.all(
          [169, 170, 171].map(async (id, index) => {
            const title = await generateProjectTitle(
              undefined,
              false,
              undefined,
              [],
            )
            return {
              createdAt: '2024-01-01',
              isPublic: !(index % 2),
              missionId: `${id}`,
              projectId: `${id}`,
              robloxUserId: 'exampleRobloxUserId',
              title: index === 0 ? title : `${title}-${index}`,
              updatedAt: '2024-01-01',
              userId: '1',
            }
          }),
        )
        resolve(projects)
      }, 1000)
    })
  }

  const endpoint = `${import.meta.env.VITE_LAMBDA_ENDPOINT}?uid=${userId}`
  const res = await fetch(endpoint)
  if (res.ok) {
    const data = await res.json()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return data.map((d: any) => ({
      createdAt: d.created_at,
      isPublic: !!d.is_public,
      missionId: d.mission_id,
      projectId: d.project_id,
      robloxUserId: d.roblox_user_id,
      title: d.title,
      updatedAt: d.updated_at,
      userId: d.user_id,
      isCopy: d.is_copy,
      originalId: d.original_id,
    }))
  } else {
    console.error('Failed to fetch project list:', res)
  }

  throw new Error('Failed to fetch project list')
}

/**
 * 指定されたmissionIdに該当する既存プロジェクトを検索する
 * 複数ある場合は最新のものを返す
 */
export const findExistingProject = async (
  missionId: string,
): Promise<string | null> => {
  if (location.hostname === 'localhost') {
    return null
  }
  const endpoint = `${import.meta.env.VITE_LAMBDA_ENDPOINT}/current?mission_id=${missionId}`

  try {
    const res = await fetch(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    })

    if (res.ok) {
      const data = await res.json()
      return data.project_id || null
    } else {
      console.error('Failed to find existing project:', res)
      return null
    }
  } catch (e) {
    console.error('Error finding existing project:', e)
    return null
  }
}

export const getProjectInfo = async (
  projectId: string,
): Promise<ShortProjectInfo | null> => {
  if (location.hostname === 'localhost') {
    const title = await generateProjectTitle(undefined, false, undefined, [])
    return {
      isPublic: false,
      missionId: `${projectId}`, //localではmissionId===projectIdになる
      title: title,
      userId: '1',
    }
  }

  const endpoint = `${import.meta.env.VITE_LAMBDA_ENDPOINT}/${projectId}`
  const res = await fetch(endpoint)
  if (res.ok) {
    const data = await res.json()
    if (
      data.length > 0 &&
      Object.prototype.hasOwnProperty.call(data[0], 'mission_id') &&
      Object.prototype.hasOwnProperty.call(data[0], 'title') &&
      Object.prototype.hasOwnProperty.call(data[0], 'user_id')
    ) {
      return {
        isPublic: data[0].is_public,
        missionId: `${data[0].mission_id}`,
        title: data[0].title,
        userId: data[0].user_id,
        isCopy: data[0].is_copy,
        originalId: data[0].original_id,
      }
    }
  }

  return null
}

export const getProjectWorkspaceData = async (
  projectId: string,
): Promise<Record<string, Partial<MissionObject>> | null> => {
  if (location.hostname === 'localhost') {
    return {}
  }

  const endpoint = `${import.meta.env.VITE_LAMBDA_ENDPOINT}/${projectId}/s3`

  try {
    const res = await fetch(endpoint)
    if (res.ok) {
      const data = await res.json()
      const workspaces = Object.fromEntries(
        Object.entries(data.workspaces ?? {}).map(([name, workspace]) => [
          name,
          { workspace } as Partial<MissionObject>,
        ]),
      )
      return workspaces
    }
  } catch (e) {
    console.error('Error fetching workspace data:', e)
  }

  return null
}

export const saveProjectWorkspaceData = async (
  projectId: string,
  workspaceData: Record<string, Record<string, unknown>>,
): Promise<void> => {
  if (location.hostname === 'localhost') {
    return
  }

  const endpoint = `${import.meta.env.VITE_LAMBDA_ENDPOINT}/${projectId}/s3`
  const res = await fetch(endpoint, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(workspaceData),
  })
  if (!res.ok) {
    console.error('Failed to save workspace data:', res)
    throw new Error('Failed to save workspace data')
  }
}

/**
 * Studio connection status via HTTPS (school networks block wss, so this is the
 * authoritative connection signal). Returns the server's freshness verdict on
 * the studio heartbeat. Never throws; returns `null` on network/HTTP error so
 * the caller can distinguish a transient failure from a genuine "disconnected".
 * Failures are recorded as structured Sentry breadcrumbs for diagnosis.
 *
 * Note: intentionally does NOT early-return on localhost — the polling path must
 * be exercisable against a local SAM endpoint.
 */
// Bound each poll so a hung connection always settles. Without this the
// polling loop's in-flight guard would block every subsequent poll, so a
// dropped connection could never be re-detected without a reload.
export const CONNECTION_STATUS_TIMEOUT_MS = 10000

export const getConnectionStatus = async (
  projectId: string,
): Promise<boolean | null> => {
  const endpoint = `${import.meta.env.VITE_LAMBDA_ENDPOINT}/${projectId}/connection-status`

  let token = ''
  try {
    token = getToken()
  } catch {
    token = ''
  }

  const controller = new AbortController()
  const timeout = setTimeout(
    () => controller.abort(),
    CONNECTION_STATUS_TIMEOUT_MS,
  )
  try {
    const res = await fetch(endpoint, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: controller.signal,
    })
    if (!res.ok) {
      addBreadcrumb({
        category: 'linkage',
        level: 'warning',
        message: `connection-status http ${res.status}`,
        data: { projectId, status: res.status },
      })
      return null
    }
    const data = await res.json()
    return !!data.connected
  } catch (e) {
    addBreadcrumb({
      category: 'linkage',
      level: 'warning',
      message: 'connection-status network error',
      data: { projectId, error: String(e) },
    })
    return null
  } finally {
    clearTimeout(timeout)
  }
}

/**
 * Vite アセット URL からテンプレート名を取り出す。
 *
 * ミッションモジュールは `import template from './templates/RJF01.rbxl'` で
 * アセット URL を得る。その URL は環境で形が変わる:
 *   - dev:   `/src/missions/templates/RJF01.rbxl`
 *   - build: `/assets/RJF01-aB3dE9_x.rbxl`
 *            （Vite デフォルト assetFileNames `[name]-[hash][extname]`、
 *             ハッシュは 8 文字 `[A-Za-z0-9_-]`。vite.config.ts はカスタム命名なし）
 *
 * サーバは S3 の `templates/{name}.rbxl` を引くため、ここで導出した name を
 * クエリで渡す。クエリ/フラグメントを除いたパス末尾のファイル名を、
 * 「名前本体 + 任意の `-<8文字ハッシュ>` + `.rbxl` 拡張子（必須）」として
 * 単一の正規表現で厳密に照合し、想定外の形は null を返す（誤った名前で
 * リクエストして紛らわしい 404 を生むより、導出失敗を明示する）。
 *
 * 既存テンプレート名は RJF01〜RJF12, RJS01〜RJS12, Event01, RJF01_trial 系で、
 * アンダースコアは含み得るがハイフンは含まない。よって名前本体を
 * `[A-Za-z0-9_]+`（ハイフン無し）に限定でき、ハッシュとの境界が曖昧にならない。
 */
export const extractTemplateName = (templateUrl: string): string | null => {
  // クエリ/フラグメントを除去する。
  const withoutQuery = templateUrl.split(/[?#]/)[0]
  // パス末尾のファイル名を取り出す。
  const fileName = withoutQuery.split('/').pop() ?? ''
  // 「名前本体（ハイフン無し）+ 任意の `-<8文字ハッシュ>` + `.rbxl`」に厳密一致。
  const match = fileName.match(/^([A-Za-z0-9_]+)(?:-[A-Za-z0-9_-]{8})?\.rbxl$/)
  return match?.[1] ?? null
}

/** Blob/URL をファイルとしてダウンロードさせる（DOM 副作用）。 */
const triggerBrowserDownload = (href: string, filename: string): void => {
  const elem = document.createElement('a')
  elem.href = href
  elem.download = filename
  document.body.appendChild(elem)
  elem.click()
  elem.remove()
}

/**
 * Blockly 編集を反映した rbxl をダウンロードする。
 *
 * サーバ側で「原本 rbxl ＋ 保存済み code.json」を逐次マージし、保存せずに
 * .rbxl を返すエンドポイント (GET /project/{pid}/download) を叩く。
 * 無編集なら code.json が空のため、原本と同内容の rbxl が返る。
 *
 * localhost（マージ用 Lambda が無い開発環境）では、フォールバックとして
 * 原本テンプレートをそのままダウンロードする（従来挙動）。
 *
 * マージはサーバ処理のため時間がかかり得る。呼び出し側は共通ローディングを出すこと。
 */
export const downloadProjectRbxl = async (
  projectId: string,
  fallbackTemplateUrl: string,
  filename: string,
): Promise<void> => {
  if (location.hostname === 'localhost') {
    triggerBrowserDownload(fallbackTemplateUrl, filename)
    return
  }

  // サーバは S3 の `templates/{name}.rbxl` を引くため、DB の数値 mission_id では
  // なくテンプレート名をクエリで渡す。導出できない URL は誤ったリクエストを
  // 送らないよう即座に失敗させる。
  const templateName = extractTemplateName(fallbackTemplateUrl)
  if (!templateName) {
    throw new Error(
      `テンプレート名を特定できませんでした (${fallbackTemplateUrl})`,
    )
  }

  const endpoint = `${import.meta.env.VITE_LAMBDA_ENDPOINT}/${projectId}/download?template=${encodeURIComponent(templateName)}`
  // octet-stream を Accept して API Gateway にバイナリ応答を返させる。
  const res = await fetch(endpoint, {
    headers: { Accept: 'application/octet-stream' },
  })
  if (!res.ok) {
    throw new Error(
      `rbxl のダウンロードに失敗しました (${res.status} ${res.statusText})`,
    )
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  triggerBrowserDownload(url, filename)
  // 即時 revoke は一部ブラウザで DL をキャンセルし得るため、少し遅延させて解放する。
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}

export const updateProjectInfo = async (
  projectId: string,
  info: PartialProjectInfo,
) => {
  if (location.hostname === 'localhost') {
    return new Promise<ShortProjectInfo>((resolve) => {
      setTimeout(
        async () =>
          resolve({
            ...(await getProjectInfo(projectId)),
            ...info,
          } as ShortProjectInfo),
        3000,
      )
    })
  }

  const endpoint = `${import.meta.env.VITE_LAMBDA_ENDPOINT}/${projectId}`
  const res = await fetch(endpoint, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      is_public: info.isPublic ? '1' : '0',
      mission_id: info.missionId,
      title: info.title,
      user_id: info.userId,
    }),
  })

  if (!res.ok) {
    console.error('Failed to update project info:', res)
    throw new Error('Failed to update project info')
  }

  return await getProjectInfo(projectId)
}
